import React, { HTMLAttributes, useState, useEffect, useCallback } from 'react';
import '../../base.scss';
import './Select.scss';
import { Icon } from '../Icon';
import { classNames } from '../../utilities';
import useOutsideClick from '../../utilities/hooks/use-outside-click';

export interface OptionProps {
  /** key value of the option */
  key: string | number;
  /** display when this option is selected. */
  text: string;
  /** details to show in dropdown. If not set, uses text prop. */
  label?: React.ReactNode;
  /** is option disabled or not
   * @default false
   */
  disabled?: boolean;
  /** option type if it is a header or action, leave off for basic option*/
  type?: 'header' | 'action' | undefined | null;
  /** onClick function if option type is an action */
  onClick?: () => void;
}

export interface SelectProps extends HTMLAttributes<HTMLElement> {
  /** required for accessibility if no labels */
  'aria-label'?: string;
  /** Attach a custom class to the select container */
  className?: string;
  /** A ref to access the component, required to close menu on blur */
  componentRef: React.RefObject<HTMLDivElement>;
  /** selection is not clickable
   * @default false
   */
  disabled?: boolean;
  /** default value for the uncontrolled dropdown */
  defaultValue?: string;
  /** ID for the checkbox */
  id?: string;
  /** Value for controlled select */
  value?: string;
  /** set to true to display inline style instead
   * @default false
   */
  inline?: boolean;
  /** Options for the dropdown to display
   * @default []
   */
  options?: Array<OptionProps>;
  /** function to handle the change of the dropdown */
  handleChange?: (value: string) => void;
  /** Loading boolean to be passed in from parent
   * @default false
   */
  isLoading?: boolean;
  /** error message from the parent if there is an error, leave empty if no error
   * @default null
   */
  error?: string;
  /** Determines whether the Select box should show above the Select text
   * @default false
   */
  dropUp?: boolean;
}
/** linting disabled for the next line see: https://github.com/yannickcr/eslint-plugin-react/issues/2404 */
const Select = (props: SelectProps) => {
  //eslint-disable-line react/display-name
  const {
    className,
    id,
    defaultValue,
    value,
    options = [],
    handleChange,
    componentRef,
    disabled = false,
    isLoading = false,
    inline = false,
    error = null,
    dropUp = false,
  } = props;

  const ARROW_UP = 38;
  const ARROW_DOWN = 40;

  const [expanded, setExpanded] = useState(false);
  const [cursor, setCursor] = useState(-1);
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue || options[0]?.text || 'Select');

  /** checks to see if the option the cursor is on (for keyboard events)
   *  is selectable or not (is it a header or an action or disabled?)
   */
  const isOptionSelectable = (option: OptionProps) => {
    if (option?.type === 'header' || option?.type === 'action' || option?.disabled) {
      return false;
    }
    return true;
  };

  /** gets the index of the last selectable option in the list */
  const getLastSelectableIndex = () => {
    let lastSelectableIndex = -1;
    options.forEach((option, i) => {
      if (isOptionSelectable(option)) {
        lastSelectableIndex = i;
      }
    });

    return lastSelectableIndex;
  };

  /** gets the index of the first selectable option in the list */
  const getFirstSelectableIndex = () => {
    let fistSelectableIndex = -1;
    for (let i = 0; i < options.length; i++) {
      if (isOptionSelectable(options[i])) {
        fistSelectableIndex = i;
        break;
      }
    }
    return fistSelectableIndex;
  };

  /** finds the index of next selectable item */
  const findNextSelectableIndex = (arrowDirection: number) => {
    let index = -1;
    if (arrowDirection === ARROW_DOWN) {
      index = cursor + 1;
      while (!isOptionSelectable(options[index])) {
        index++;
      }
      if (index >= options?.length) {
        index = getLastSelectableIndex();
      }
    } else if (arrowDirection === ARROW_UP) {
      index = cursor - 1;
      while (!isOptionSelectable(options[index])) {
        index--;
      }
      if (index < 0) {
        index = getFirstSelectableIndex();
      }
    }
    return index;
  };

  /** sets state to open and close the menu */
  const toggleMenu = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);

  /** listens to see if the user clicks off of the dropdown
   *  closes the dropdown if they do
   */
  useOutsideClick(componentRef, () => setExpanded(false));

  /** handles setting the selected value of the dropdown */
  const handleSelect = useCallback(
    (event: { type: string }, value?: string) => {
      if (event?.type === 'keydown') {
        handleChange ? handleChange(value || '') : setUncontrolledValue(value || '');
      } else {
        handleChange ? handleChange(value || '') : setUncontrolledValue(value || '');
        toggleMenu();
      }
    },
    [handleChange, toggleMenu]
  );

  /** listens to the cursor state variable and calls handleChange */
  useEffect(() => {
    if (cursor >= 0) {
      const event = { type: 'keydown' };
      handleSelect(event, options?.[cursor]?.text);
    }
  }, [cursor, options, handleSelect]);

  /** handles keyboard iteration over the events via up and down arrows */
  const handleKeyDown = (e: { keyCode: number; preventDefault: () => void }) => {
    // arrow up/down button should select next/previous list element
    if (!isLoading && e.keyCode === ARROW_UP && cursor > 0) {
      e.preventDefault();
      setCursor(findNextSelectableIndex(ARROW_UP));
    } else if (!isLoading && e.keyCode === ARROW_DOWN && cursor < options.length - 1) {
      e.preventDefault();
      setCursor(findNextSelectableIndex(ARROW_DOWN));
    }
  };

  /** resets the cursor when user interacts with the mouse instead */
  const handleMouseOver = () => setCursor(-1);

  /** wrapper function to close the dropdown and trigger the action onclick function */
  const handleAction = (onClick: (() => void) | undefined) => {
    toggleMenu();
    onClick && onClick();
  };

  /** turn options array into divs and buttons */
  const listboxItems = options?.map((option, index) => {
    const content: React.ReactNode = option.label ?? option.text;
    if (option.type === 'header') {
      return (
        <div className="ps-select__option-header" key={`${option.key}${index}`}>
          {content}
        </div>
      );
    } else if (option.disabled) {
      return (
        <div className="ps-select__option ps-select__option--disabled" key={`${option.key}${index}`}>
          {content}
        </div>
      );
    } else if (option.type === 'action') {
      return (
        <div
          className="ps-select__option ps-select__option--action"
          key={`${option.key}${index}`}
          onClick={() => handleAction(option.onClick)}
        >
          {content}
        </div>
      );
    }
    return (
      <button
        className={`ps-select__option ${cursor === index ? 'ps-select__option--active' : ''}`}
        key={`${option.key}${index}`}
        onClick={(e) => handleSelect(e, option.text)}
        onMouseOver={handleMouseOver}
        onKeyDown={handleKeyDown}
      >
        {content}
      </button>
    );
  });

  /** optional messages */
  const loadingMsg = <div className="ps-select__loading">Loading Options...</div>;
  const errorMsg = error && <div className="ps-select__error">{error}</div>;

  const selectClassNames = classNames('ps-select', className ?? '');

  const buttonClassNames = classNames(
    inline ? 'ps-select__button-inline' : 'ps-select__button',
    inline && error ? 'ps-select__button-inline--error' : '',
    disabled ? `ps-select__button${inline ? '-inline' : ''}--disabled` : '',
    error ? `ps-select__button--error` : ''
  );

  const arrowClassNames = classNames(
    'ps-select__arrow',
    inline ? `ps-select__arrow--inline${error ? '-error' : ''}` : '',
    disabled ? `ps-select__arrow--${inline ? 'inline-' : ''}disabled` : ''
  );

  const menuClassNames = classNames(
    'ps-select__menu',
    expanded ? 'ps-select__menu--expanded' : '',
    inline ? 'ps-select__menu--inline' : '',
    dropUp ? 'ps-select__menu--drop-up' : ''
  );

  return (
    <div className={selectClassNames} ref={componentRef} id={id}>
      <button
        aria-label={props['aria-label']}
        aria-haspopup="listbox"
        aria-expanded={expanded}
        className={buttonClassNames}
        onClick={toggleMenu}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      >
        {value || uncontrolledValue}
        <Icon className={arrowClassNames} name={dropUp ? 'SortUp' : 'SortDown'} />
      </button>
      <div className={menuClassNames} role="listbox">
        {isLoading ? loadingMsg : listboxItems}
      </div>
      {!inline && errorMsg}
    </div>
  );
};

export { Select };
